/**
 * CB-02 (2026-07-22) — Reverse materialisation.
 *
 * Companion to `materializeBuilder.js` (the forward path).
 *
 * Purpose: recover an editable Builder prototype from a version's
 * PERSISTED `line_items` when that version has NO `builder_state`
 * snapshot yet. This is the legacy-proposal path:
 *
 *   - Proposals populated BEFORE CB-01 (2026-07-22) never wrote
 *     `builder_state` to the backend, so opening their Builder used to
 *     show an empty shell (`defaultPrototype`).
 *   - Proposals cloned from such legacy versions also have `builder_state
 *     = null` on the cloned version.
 *   - Reverse-materialise reads the emission rules from
 *     `materializeBuilder.js` in reverse:
 *       • `row_kind: "product_header"` → tracks the parent product for
 *          subsequent `module` rows. No scope row is emitted for the
 *          synthetic header itself (headers are 0-price display glue).
 *       • `row_kind: "module"` → emits ONE scope row (`product_master`
 *          source) with `moduleLabel` set. Prices land on
 *          `commercials.itemPrices[scopeId]` + `amc.items`.
 *       • `row_kind: "atomic"` (or missing) → emits ONE scope row for a
 *          standalone product. Falls back to `source: "custom"` when the
 *          product isn't found in PRODUCT_MASTER.
 *       • `option_label` distinct from a product's own label AND distinct
 *          from "OTHER ITEMS" → registered as a Builder group containing
 *          every product whose lines carry that label.
 *
 * The output is best-effort — Master products are looked up by label
 * against the LIVE catalogue-hydrated `PRODUCT_MASTER` so subtitles,
 * descriptions, categoryLabel and delivery-mode chips render correctly.
 * Products that don't match a catalogue entry are recovered as `custom`
 * rows so the operator still sees the amount + AMC they persisted.
 *
 * The function is PURE — it does NOT touch localStorage. The caller
 * decides whether to persist (`upsertPrototype`) after review.
 */

import { PRODUCT_MASTER } from "./productMasterHierarchy";
import { newScopeId, newGroupId, newAmcId, defaultPrototype } from "./builderStorage";

const OTHER_ITEMS_LABEL = "OTHER ITEMS";

/**
 * Normalise a label for tolerant catalogue lookup. Trims, lower-cases,
 * and collapses EN-DASH (\u2013), EM-DASH (\u2014) and NON-BREAKING
 * HYPHEN (\u2011) down to the ASCII hyphen so `"Listed Equity - BSE"`
 * (hyphen, as legacy line_items often stored) matches
 * `"Listed Equity – BSE"` (EN-dash, as the catalogue currently emits).
 * Also normalises runs of whitespace so `"aiM-Wealth"` and
 * `"aiM-Wealth "` compare equal.
 */
function _normLabel(s) {
    return String(s || "")
        .trim()
        .toLowerCase()
        .replace(/[\u2013\u2014\u2011]/g, "-")
        .replace(/\s+/g, " ");
}

/**
 * Find a Master product by its display label. Returns `{ product,
 * category }` or `null`. Case + dash + whitespace tolerant (see
 * `_normLabel` above).
 */
function _findMasterProductByLabel(label) {
    if (!label) return null;
    const target = _normLabel(label);
    for (const top of PRODUCT_MASTER) {
        if (top.type === "category") {
            for (const p of top.products || []) {
                if (_normLabel(p.label) === target) {
                    return { product: p, category: top };
                }
            }
        } else if (top.type === "product") {
            if (_normLabel(top.label) === target) {
                return { product: top, category: null };
            }
        }
    }
    return null;
}

/**
 * Find a module by label under a given Master product. Returns the
 * module node or `null`. Case + dash + whitespace tolerant.
 */
function _findModuleByLabel(product, label) {
    if (!product || !label) return null;
    const target = _normLabel(label);
    return (product.modules || []).find(
        (m) => _normLabel(m.label) === target,
    ) || null;
}

/**
 * Build a Master-source scope row for an atomic product.
 * Mirrors `scopeRowForProduct` in ProductSelectionSection.jsx.
 */
function _scopeRowForMasterProduct(product, category) {
    return {
        id: newScopeId(),
        source: "product_master",
        kind: "service",
        capabilities: [],
        assetClass: "",
        productMasterCode: product.code,
        productCode: product.code,
        moduleCode: null,
        groupCode: product.code,
        groupLabel: product.label,
        categoryCode: category ? category.code : null,
        categoryLabel: category ? category.label : null,
        productName: product.label,
        subtitle: category ? category.label : "",
        // Complimentary marker (2026-07-23) — legacy versions have no
        // marker; default both to "priced" so existing ₹0 rows are
        // rendered as ₹0 and NEVER inferred as complimentary.
        setup_fee_pricing_mode: "priced",
        amc_pricing_mode: "priced",
    };
}

/**
 * Build a Master-source scope row for a module under a product.
 * Mirrors `scopeRowForModule` in ProductSelectionSection.jsx.
 */
function _scopeRowForMasterModule(product, module, category) {
    return {
        id: newScopeId(),
        source: "product_master",
        kind: "service",
        capabilities: [],
        assetClass: "",
        productMasterCode: module.code,
        productCode: product.code,
        moduleCode: module.code,
        groupCode: product.code,
        groupLabel: product.label,
        categoryCode: category ? category.code : null,
        categoryLabel: category ? category.label : null,
        productName: module.label,
        subtitle: category
            ? `${category.label} · ${product.label}`
            : product.label,
        moduleLabel: module.label,
        // Complimentary marker (2026-07-23) — legacy versions have no
        // marker; default to "priced" (see _scopeRowForMasterProduct).
        setup_fee_pricing_mode: "priced",
        amc_pricing_mode: "priced",
    };
}

/**
 * Custom (non-master) scope row for a product name that isn't in the
 * catalogue. Preserves the amount so the operator sees the persisted
 * numbers and can either rename to match the catalogue or leave as
 * custom.
 */
function _scopeRowForCustom(productName, description = "") {
    return {
        id: newScopeId(),
        source: "custom",
        kind: "service",
        capabilities: [],
        assetClass: "",
        productMasterCode: null,
        productCode: null,
        moduleCode: null,
        groupCode: null,
        groupLabel: null,
        categoryCode: null,
        categoryLabel: null,
        productName: productName || "Custom Item",
        subtitle: "",
        // Owner directive 2026-09-29 — round-trip the operator-written
        // product note so reopening the Builder on a saved version does
        // not silently drop it.
        description: description || "",
        // Complimentary marker (2026-07-23) — legacy versions have no
        // marker; default to "priced" (see _scopeRowForMasterProduct).
        setup_fee_pricing_mode: "priced",
        amc_pricing_mode: "priced",
    };
}

/**
 * Reverse-materialise a Builder prototype from persisted line_items.
 *
 * @param {Array}  lineItems         The version's line_items array.
 * @param {Object} opts
 * @param {string} opts.prototypeId   The prototype key to stamp on the
 *                                    returned prototype (localStorage id).
 * @param {string} opts.title         Human-readable proposal title.
 * @param {string} opts.clientName    Client name (display only).
 * @param {string} opts.currency      Currency code (defaults INR).
 *
 * @returns {Object} A prototype compatible with `defaultPrototype` +
 *   `upsertPrototype`. `_recovered` flag is set to true so the UI can
 *   surface a "Recovered from applied line items" hint.
 */
export function reverseMaterializeFromLineItems(lineItems, opts = {}) {
    const {
        prototypeId,
        title = "Proposal",
        clientName = "",
        currency = "INR",
    } = opts;

    // Fallback: no line_items → return a fresh default prototype.
    const items = Array.isArray(lineItems) ? lineItems : [];
    if (items.length === 0) {
        return defaultPrototype({
            id: prototypeId,
            title,
            clientName,
            currency,
        });
    }

    const scope = [];
    const itemPrices = {};
    const annualCostPrices = {};
    const amcItems = [];
    // Annual Cost (2026-10) — restore the per-item recurring kind + amount.
    const restoreRecurring = (row, li) => {
        if (li.amc_pricing_mode === "complimentary") row.amc_pricing_mode = "complimentary";
        const acComp = li.annual_cost_pricing_mode === "complimentary";
        const ac = Number(li.annual_cost_amount) || 0;
        if (!acComp && ac <= 0) return;
        row.recurring_kind = "annual_cost";
        row.annual_cost_pricing_mode = acComp ? "complimentary" : "priced";
        if (!acComp) annualCostPrices[row.id] = ac;
    };
    const deliveryModes = {}; // productKey -> [modes]
    const groupsByLabel = new Map(); // label -> { id, label, productKeys:Set, price:0, amc:0 }
    // Track products encountered so their FIRST option_label wins for group
    // assignment (matches the forward path's `groupByProductKey` first-write
    // semantics).
    const productKeyToOptionLabel = new Map();

    /* Presentation mode: read from any line (all lines carry the same
       mode per the forward emission rules). GST + tax display too. */
    const firstLine = items.find((li) => li && li.presentation_mode) || items[0] || {};
    const presentationMode = firstLine.presentation_mode || "itemized";
    const gstApplicableAny = items.some((li) => li && li.gst_applicable);
    const gstPercent =
        Number(items.find((li) => Number(li?.gst_percent) > 0)?.gst_percent) || 18;

    let currentHeader = null; // { productKey, product, category, productName }

    items.forEach((li) => {
        if (!li) return;
        const rowKind = li.row_kind || "atomic";
        const productName = li.product_name || "";
        const optionLabel = li.option_label || OTHER_ITEMS_LABEL;
        const amount = Number(li.amount) || 0;
        const amcAmount = Number(li.amc_amount) || 0;
        const modes = Array.isArray(li.delivery_modes) ? li.delivery_modes.slice() : [];

        if (rowKind === "product_header") {
            /* Synthetic header — establishes the parent product for the
               `module` rows that follow. Look up in PRODUCT_MASTER;
               fall back to custom if unrecognised. */
            const found = _findMasterProductByLabel(productName);
            if (found) {
                currentHeader = {
                    productKey: `master:${found.product.code}`,
                    product: found.product,
                    category: found.category,
                    productName,
                    optionLabel,
                    isCustom: false,
                };
                if (modes.length && !found.product.noDeliveryModes) {
                    deliveryModes[currentHeader.productKey] = modes;
                }
            } else {
                // Custom multi-module — we can't reconstruct modules
                // structurally without master modules. We'll emit
                // custom scope rows for each following `module` line.
                currentHeader = {
                    productKey: `custom:header-${scope.length}-${productName}`,
                    product: null,
                    category: null,
                    productName,
                    optionLabel,
                    isCustom: true,
                };
                if (modes.length) {
                    deliveryModes[currentHeader.productKey] = modes;
                }
            }
            // Header row itself doesn't create a scope row (0 price).
            return;
        }

        if (rowKind === "module") {
            /* Module under the current product_header. If the header
               was a Master product, look up the module by label. */
            let row;
            let productKey;
            if (currentHeader && !currentHeader.isCustom) {
                const mod = _findModuleByLabel(currentHeader.product, productName);
                if (mod) {
                    row = _scopeRowForMasterModule(
                        currentHeader.product,
                        mod,
                        currentHeader.category,
                    );
                    productKey = `master:${currentHeader.product.code}`;
                } else {
                    // Module label doesn't match catalogue — degrade to
                    // custom row but retain the parent product name in
                    // subtitle so context isn't lost.
                    row = _scopeRowForCustom(productName);
                    row.subtitle = currentHeader.productName;
                    productKey = `custom:${row.id}`;
                }
            } else if (currentHeader && currentHeader.isCustom) {
                row = _scopeRowForCustom(productName);
                row.subtitle = currentHeader.productName;
                productKey = `custom:${row.id}`;
            } else {
                // Orphan module row (no preceding header) — treat as
                // standalone atomic-like recovery.
                row = _scopeRowForCustom(productName);
                productKey = `custom:${row.id}`;
            }

            scope.push(row);
            itemPrices[row.id] = amount;
            restoreRecurring(row, li);
            if (amcAmount > 0) {
                amcItems.push({
                    id: newAmcId(),
                    scopeRowId: row.id,
                    amount: amcAmount,
                });
            }
            _registerOption(productKey, optionLabel, groupsByLabel, productKeyToOptionLabel);
            return;
        }

        /* Atomic row (or missing row_kind) — one standalone product. */
        const found = _findMasterProductByLabel(productName);
        let row;
        let productKey;
        if (found) {
            row = _scopeRowForMasterProduct(found.product, found.category);
            productKey = `master:${found.product.code}`;
            if (modes.length && !found.product.noDeliveryModes) {
                deliveryModes[productKey] = deliveryModes[productKey] || modes;
            }
        } else {
            row = _scopeRowForCustom(productName, li.description);
            productKey = `custom:${row.id}`;
            if (modes.length) {
                deliveryModes[productKey] = deliveryModes[productKey] || modes;
            }
        }
        scope.push(row);
        itemPrices[row.id] = amount;
        restoreRecurring(row, li);
        if (amcAmount > 0) {
            amcItems.push({
                id: newAmcId(),
                scopeRowId: row.id,
                amount: amcAmount,
            });
        }
        _registerOption(productKey, optionLabel, groupsByLabel, productKeyToOptionLabel);
        // Header carry-over resets after an atomic row (atomics aren't
        // parented by a header).
        currentHeader = null;
    });

    /* Convert groups map → array with productKeys as arrays. Skip the
       "OTHER ITEMS" bucket (that's the atomic fallback label, not a
       real Builder group). */
    const groups = [];
    groupsByLabel.forEach((g, label) => {
        if (String(label).trim().toUpperCase() === OTHER_ITEMS_LABEL) return;
        groups.push({
            id: g.id,
            label: g.label,
            productKeys: Array.from(g.productKeys),
            price: 0,
            amc: 0,
        });
    });

    const proto = defaultPrototype({
        id: prototypeId,
        title,
        clientName,
        currency,
    });
    proto.scope = scope;
    proto.commercials = {
        ...proto.commercials,
        presentationMode,
        itemPrices,
        annualCostPrices,
        groups,
        deliveryModes,
    };
    proto.amc = { items: amcItems };
    proto.taxDisplay = {
        mode: gstApplicableAny ? "show_gst" : "taxes_extra",
        gstPercent,
    };
    // Marker flag consumed by the UI to show a "Recovered from applied
    // line items" hint. NOT sent to backend on Apply (stripped by the
    // Apply payload builder alongside the localStorage `id`).
    proto._recovered = true;
    proto._recoveredAt = new Date().toISOString();
    return proto;
}

function _registerOption(productKey, optionLabel, groupsByLabel, productKeyToOptionLabel) {
    if (!productKey) return;
    // First-write wins per product.
    if (!productKeyToOptionLabel.has(productKey)) {
        productKeyToOptionLabel.set(productKey, optionLabel);
    }
    const label = productKeyToOptionLabel.get(productKey);
    if (!label) return;
    if (String(label).trim().toUpperCase() === OTHER_ITEMS_LABEL) return;
    if (!groupsByLabel.has(label)) {
        groupsByLabel.set(label, {
            id: newGroupId(),
            label,
            productKeys: new Set(),
        });
    }
    groupsByLabel.get(label).productKeys.add(productKey);
}
